import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import {
  LockClosedIcon, EyeIcon, EyeSlashIcon, ClipboardDocumentIcon,
  MagnifyingGlassIcon, PlusIcon, XMarkIcon, PencilSquareIcon, TrashIcon,
  LinkIcon, EnvelopeIcon, PhoneIcon, KeyIcon,
  ClockIcon, UserIcon,
} from '@heroicons/react/24/outline';

// Account Vault — shared director + admin storage for system logins
// (iLEAD, WellNow, Identogo, banking, insurance portals, etc.).
// Passwords encrypted at rest; reveal action logs to audit table.

export default function AccountVaultPage() {
  const { user } = useAuth();
  const isDirector = user?.role === 'director';
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [historyOf, setHistoryOf] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (categoryFilter !== 'all') params.set('category', categoryFilter);
      if (search) params.set('search', search);
      const { data } = await api.get(`/account-vault?${params}`);
      setAccounts(data);
    } catch { toast.error('Failed to load vault'); }
    finally { setLoading(false); }
  }, [categoryFilter, search]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/account-vault/categories').then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  const onSave = async (form) => {
    try {
      if (editing && editing.id) {
        await api.patch(`/account-vault/${editing.id}`, form);
        toast.success('Updated');
      } else {
        await api.post('/account-vault', form);
        toast.success('Account added');
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    }
  };

  const onDelete = async (id) => {
    if (!isDirector) return toast.error('Director only.');
    if (!window.confirm('Delete this account from the vault? This can\'t be undone.')) return;
    try {
      await api.delete(`/account-vault/${id}`);
      toast.success('Deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  };

  // Group by category for display
  const grouped = {};
  for (const a of accounts) {
    const key = a.category || 'Other';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(a);
  }
  const groupKeys = Object.keys(grouped).sort((a, b) => {
    if (a === 'Other') return 1;
    if (b === 'Other') return -1;
    return a.localeCompare(b);
  });

  return (
    <div className="animate-slide-up max-w-5xl mx-auto space-y-5">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 inline-flex items-center gap-2">
            <LockClosedIcon className="w-6 h-6" /> Account Vault
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Shared system logins for director + admin. Passwords are encrypted at rest. Every reveal is logged.
          </p>
        </div>
        <button onClick={() => setEditing({})}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-orange-500 hover:bg-orange-600 px-4 py-2 rounded-xl flex-shrink-0">
          <PlusIcon className="w-4 h-4" /> Add account
        </button>
      </header>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-2xl p-4 flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px] relative">
          <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search label, username, email, URL, notes…"
            className="w-full pl-9 pr-3 py-1.5 text-sm rounded-lg border border-gray-200 focus:border-orange-500 focus:outline-none" />
        </div>
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
          className="text-sm rounded-lg border border-gray-200 px-3 py-1.5 bg-white">
          <option value="all">All categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>
      ) : accounts.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center">
          <KeyIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">No accounts yet.</p>
          <p className="text-sm text-gray-500 mt-1">Click "Add account" to add your first one.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {groupKeys.map(category => (
            <div key={category} className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
              <div className="px-5 py-2.5 bg-gray-50 border-b border-gray-100">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  {category} <span className="text-gray-400 font-normal">· {grouped[category].length}</span>
                </p>
              </div>
              <ul className="divide-y divide-gray-100">
                {grouped[category].map(a => (
                  <AccountRow key={a.id} account={a}
                    onEdit={() => setEditing(a)}
                    onDelete={() => onDelete(a.id)}
                    onShowHistory={() => setHistoryOf(a)}
                    canDelete={isDirector} />
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <AccountModal
          account={editing}
          categories={categories}
          onSave={onSave}
          onCancel={() => setEditing(null)}
        />
      )}
      {historyOf && (
        <RevealHistoryModal account={historyOf} onCancel={() => setHistoryOf(null)} />
      )}
    </div>
  );
}

function AccountRow({ account, onEdit, onDelete, onShowHistory, canDelete }) {
  const [revealed, setRevealed] = useState(null);
  const [revealing, setRevealing] = useState(false);

  const reveal = async () => {
    if (revealed) { setRevealed(null); return; }
    if (!account.has_password) return toast.error('No password on file.');
    setRevealing(true);
    try {
      const { data } = await api.post(`/account-vault/${account.id}/reveal`);
      setRevealed(data.password);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to reveal');
    } finally { setRevealing(false); }
  };

  const copyPassword = async () => {
    if (!account.has_password) return toast.error('No password on file.');
    try {
      const { data } = await api.post(`/account-vault/${account.id}/reveal`);
      await navigator.clipboard.writeText(data.password);
      toast.success('Password copied to clipboard');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to copy');
    }
  };

  const copy = async (text, label) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch { toast.error('Copy failed'); }
  };

  return (
    <li className="px-5 py-3 hover:bg-gray-50/50 group">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-gray-900">{account.label}</p>
            {account.url && (
              <a href={account.url} target="_blank" rel="noopener noreferrer"
                className="text-xs text-orange-600 hover:text-orange-700 inline-flex items-center gap-1">
                <LinkIcon className="w-3.5 h-3.5" /> Open
              </a>
            )}
          </div>
          <div className="mt-1.5 grid sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
            {account.username && (
              <div className="inline-flex items-center gap-1 text-gray-700">
                <UserIcon className="w-3.5 h-3.5 text-gray-400" />
                <span className="font-mono">{account.username}</span>
                <button onClick={() => copy(account.username, 'Username')}
                  className="text-gray-400 hover:text-orange-600">
                  <ClipboardDocumentIcon className="w-3 h-3" />
                </button>
              </div>
            )}
            {account.email && (
              <div className="inline-flex items-center gap-1 text-gray-700">
                <EnvelopeIcon className="w-3.5 h-3.5 text-gray-400" />
                <a href={`mailto:${account.email}`} className="hover:text-orange-600">{account.email}</a>
                <button onClick={() => copy(account.email, 'Email')}
                  className="text-gray-400 hover:text-orange-600">
                  <ClipboardDocumentIcon className="w-3 h-3" />
                </button>
              </div>
            )}
            {account.phone && (
              <div className="inline-flex items-center gap-1 text-gray-700">
                <PhoneIcon className="w-3.5 h-3.5 text-gray-400" />
                <a href={`tel:${account.phone}`} className="hover:text-orange-600">{account.phone}</a>
              </div>
            )}
            {account.has_password && (
              <div className="inline-flex items-center gap-1 text-gray-700">
                <KeyIcon className="w-3.5 h-3.5 text-gray-400" />
                {revealed ? (
                  <span className="font-mono text-amber-700">{revealed}</span>
                ) : (
                  <span className="font-mono text-gray-400">••••••••••</span>
                )}
                <button onClick={reveal} disabled={revealing}
                  className="text-gray-400 hover:text-orange-600 disabled:opacity-50"
                  title={revealed ? 'Hide password' : 'Reveal password (logged)'}>
                  {revealed
                    ? <EyeSlashIcon className="w-3.5 h-3.5" />
                    : <EyeIcon className="w-3.5 h-3.5" />}
                </button>
                <button onClick={copyPassword}
                  className="text-gray-400 hover:text-orange-600"
                  title="Copy password (logged)">
                  <ClipboardDocumentIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
          {account.notes && (
            <p className="text-xs text-gray-500 mt-2 whitespace-pre-wrap">{account.notes}</p>
          )}
          {account.updated_by_name && account.updated_at && (
            <p className="text-[10px] text-gray-400 mt-2 inline-flex items-center gap-1">
              <ClockIcon className="w-3 h-3" />
              Updated {new Date(account.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} by {account.updated_by_name}
              {account.has_password && (
                <>
                  {' · '}
                  <button onClick={onShowHistory} className="text-orange-600 hover:text-orange-700">
                    Reveal history
                  </button>
                </>
              )}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
          <button onClick={onEdit} title="Edit"
            className="p-1.5 rounded hover:bg-gray-200 text-gray-500 hover:text-gray-700">
            <PencilSquareIcon className="w-4 h-4" />
          </button>
          {canDelete && (
            <button onClick={onDelete} title="Delete"
              className="p-1.5 rounded hover:bg-red-50 text-gray-500 hover:text-red-600">
              <TrashIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

function AccountModal({ account, categories, onSave, onCancel }) {
  const [form, setForm] = useState({
    label: account.label || '',
    category: account.category || '',
    url: account.url || '',
    username: account.username || '',
    email: account.email || '',
    phone: account.phone || '',
    password: '', // never pre-fill
    notes: account.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const isNew = !account.id;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) return toast.error('Label is required.');
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-base font-semibold text-gray-900">
            {isNew ? 'Add account' : 'Edit account'}
          </h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Label" required>
            <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="e.g. iLEAD Portal" autoFocus
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
          </Field>
          <Field label="Category">
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm bg-white">
              <option value="">— Pick one —</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="URL">
            <input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder="https://…"
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Username">
              <input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}
                className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
            </Field>
            <Field label="Email">
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
            </Field>
          </div>
          <Field label="Phone">
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="(317) 555-1234"
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
          </Field>
          <Field label={isNew ? 'Password' : 'Password (leave blank to keep current)'}>
            <input type="text" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder={isNew ? '' : '••••••••••'}
              autoComplete="off"
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-mono" />
            <p className="text-[10px] text-gray-400 mt-1">
              Encrypted with AES-256-GCM at rest. Reveal action is logged.
            </p>
          </Field>
          <Field label="Notes">
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              placeholder="Anything worth remembering — backup contact, MFA recovery, expiration date, etc."
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm resize-y" />
          </Field>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onCancel}
              className="text-sm px-3 py-1.5 rounded-lg text-gray-600 hover:bg-gray-100">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="text-sm px-4 py-1.5 rounded-lg bg-orange-500 text-white font-semibold hover:bg-orange-600 disabled:opacity-50">
              {saving ? 'Saving…' : (isNew ? 'Add account' : 'Save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RevealHistoryModal({ account, onCancel }) {
  const [reveals, setReveals] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/account-vault/${account.id}/reveals`)
      .then(({ data }) => setReveals(data))
      .catch(() => toast.error('Failed to load history'))
      .finally(() => setLoading(false));
  }, [account.id]);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Reveal history</h3>
            <p className="text-xs text-gray-500 mt-0.5">{account.label}</p>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        {loading ? (
          <div className="text-center py-6 text-sm text-gray-400">Loading…</div>
        ) : reveals.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-6">No reveals yet.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {reveals.map((r, i) => (
              <li key={i} className="py-2 text-sm flex items-center justify-between">
                <span className="text-gray-800">
                  {r.by_name || 'Unknown'}
                  {r.by_role && <span className="text-gray-400 text-xs"> · {r.by_role}</span>}
                </span>
                <span className="text-xs text-gray-500">
                  {new Date(r.revealed_at).toLocaleString('en-US', {
                    month: 'short', day: 'numeric', year: 'numeric',
                    hour: 'numeric', minute: '2-digit',
                  })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">
        {label}{required && <span className="text-red-500"> *</span>}
      </label>
      {children}
    </div>
  );
}
